import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Eye, 
  Edit, 
  Trash2, 
  Search,
  Filter,
  Calendar,
  DollarSign,
  Briefcase,
  Plus,
  MoreVertical,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Upload,
  X
} from "lucide-react";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { 
  useAdminTenders, 
  useAdminTender, 
  useCreateTender, 
  useUpdateTender, 
  useDeleteTender,
  useAdminSpecializations,
  useAdminClients,
  useAddTenderDocuments
} from "@/hooks/api/use-admin";
import type { Tender, TenderFilters, CreateTenderRequest } from "@/api/types";
import { cn } from "@/lib/utils";

const AdminTenders = () => {
  const { t, language, isRTL } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isAdmin = user?.type === 'admin';

  // State for filters and search
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'closed' | 'pending'>('all');
  const [specializationFilter, setSpecializationFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [selectedTenderId, setSelectedTenderId] = useState<number | null>(null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [specializationSearch, setSpecializationSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const observerTarget = useRef<HTMLDivElement>(null);

  // Open edit dialog if ID is in URL
  useEffect(() => {
    if (id) {
      const tenderId = parseInt(id);
      if (!isNaN(tenderId)) {
        setSelectedTenderId(tenderId);
        setIsEditDialogOpen(true);
      }
    }
  }, [id]);

  // Form state
  const [formData, setFormData] = useState<CreateTenderRequest>({
    title: { en: '', ar: '' },
    description: { en: '', ar: '' },
    requirements: { en: '', ar: '' },
    deadline: '',
    status: 'pending',
    visibility_type: 'all',
    specialization_ids: [],
    client_ids: [],
    documents: [] as File[],
  });

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filters for API
  const filters: TenderFilters = {
    per_page: 15,
    ...(statusFilter !== 'all' && { status: statusFilter }),
    ...(specializationFilter !== 'all' && { specialization_id: parseInt(specializationFilter) }),
    ...(debouncedSearchQuery && { search: debouncedSearchQuery }),
  };

  // API hooks
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useAdminTenders(
    filters,
    { enabled: isAdmin && !!user }
  );
  const { data: tenderDetails, isLoading: detailsLoading } = useAdminTender(
    selectedTenderId || 0,
    { enabled: !!selectedTenderId && isDetailsDialogOpen }
  );
  const { data: specializations } = useAdminSpecializations({ enabled: isAdmin && !!user });
  const { data: clients } = useAdminClients({}, { enabled: isAdmin && !!user });

  // Handle successful tender creation/update
  const handleTenderSuccess = () => {
    setIsCreateDialogOpen(false);
    setIsEditDialogOpen(false);
    setSelectedTenderId(null);
    resetForm();
  };

  const createMutation = useCreateTender(handleTenderSuccess);
  const updateMutation = useUpdateTender(handleTenderSuccess);
  const deleteMutation = useDeleteTender();
  const addDocumentsMutation = useAddTenderDocuments(handleTenderSuccess);

  // Flatten paginated data
  const tenders = data?.pages?.flatMap((page) => {
    const pageData = page?.data;
    if (!pageData) return [];
    
    if (Array.isArray(pageData.data)) {
      return pageData.data;
    }
    
    if (Array.isArray(pageData)) {
      return pageData;
    }
    
    return [];
  }) || [];

  // Get specializations and clients data (simplified for now)
  const specializationsList = Array.isArray(specializations?.data) ? specializations.data : [];
  const clientsList = Array.isArray(clients?.pages?.[0]?.data?.data) ? clients.pages[0].data.data : [];

  // Filtered lists based on search
  const filteredSpecializations = specializationsList.filter((spec: any) => {
    const searchLower = specializationSearch.toLowerCase();
    const name = typeof spec.name === 'string' ? spec.name : spec.name[language] || spec.name.en || '';
    return name.toLowerCase().includes(searchLower);
  });

  const filteredClients = clientsList.filter((client: any) => {
    const searchLower = clientSearch.toLowerCase();
    const name = client.name || client.company_name || '';
    const email = client.email || '';
    return name.toLowerCase().includes(searchLower) || email.toLowerCase().includes(searchLower);
  });

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Helper functions
  const getTranslatedText = (field: string | { en: string; ar: string }) => {
    if (typeof field === 'string') return field;
    return field[language] || field.en || '';
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: {
        label: t('common.active') || 'نشط',
        className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        icon: CheckCircle,
      },
      pending: {
        label: t('common.pending') || 'قيد المراجعة',
        className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        icon: Clock,
      },
      closed: {
        label: t('common.closed') || 'مغلق',
        className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
        icon: XCircle,
      },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={cn(config.className, "flex items-center gap-1")}>
        <Icon size={12} />
        {config.label}
      </Badge>
    );
  };

  // Action handlers
  const handleViewDetails = (tenderId: number) => {
    navigate(`/admin/dashboard/tenders/${tenderId}`);
  };

  const handleEdit = (tender: Tender) => {
    setSelectedTenderId(tender.id);
    setFormData({
      title: typeof tender.title === 'string' ? { en: tender.title, ar: tender.title } : tender.title,
      description: typeof tender.description === 'string' ? { en: tender.description || '', ar: tender.description || '' } : tender.description || { en: '', ar: '' },
      requirements: typeof tender.requirements === 'string' ? { en: tender.requirements || '', ar: tender.requirements || '' } : tender.requirements || { en: '', ar: '' },
      deadline: tender.deadline,
      status: tender.status,
      visibility_type: tender.visibility_type,
      specialization_ids: tender.specialization_ids || [],
      client_ids: tender.client_ids || [],
      documents: [] as File[],
    });
    setIsEditDialogOpen(true);
  };

  const handleDelete = (tenderId: number) => {
    if (confirm(t('tenders.confirmDelete') || 'هل أنت متأكد من حذف هذه المناقصة؟')) {
      deleteMutation.mutate(tenderId);
    }
  };

  const handleStatusChange = (tenderId: number, newStatus: 'active' | 'closed' | 'pending') => {
    updateMutation.mutate({
      id: tenderId,
      data: { status: newStatus }
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedTenderId) {
      // For updates, handle documents separately
      const { documents, ...tenderData } = formData;
      
      // Update tender data without files
      updateMutation.mutate({
        id: selectedTenderId,
        data: tenderData
      });
      
      // If there are new documents, upload them separately
      if (documents && documents.length > 0) {
        addDocumentsMutation.mutate({
          tenderId: selectedTenderId,
          documents,
          documentTypes: documents.map(() => 'other')
        });
      }
    } else {
      // For creation, include documents in the request
      createMutation.mutate(formData);
    }
  };

  const resetForm = () => {
    setFormData({
      title: { en: '', ar: '' },
      description: { en: '', ar: '' },
      requirements: { en: '', ar: '' },
      deadline: '',
      status: 'pending',
      visibility_type: 'all',
      specialization_ids: [],
      client_ids: [],
      documents: [],
    });
    setSelectedTenderId(null);
    // Clear search fields
    setSpecializationSearch('');
    setClientSearch('');
  };

  // Only admin can access
  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            {t('admin.tenders.adminOnly') || 'هذه الصفحة متاحة فقط للمسؤولين'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 overflow-x-hidden">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-900 dark:text-slate-100 truncate">
                {t('admin.tenders.title') || 'إدارة المناقصات'}
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1 truncate">
                {t('admin.tenders.subtitle') || 'عرض وإدارة جميع المناقصات في النظام'}
              </p>
            </div>
            <Button 
              onClick={() => { resetForm(); setIsCreateDialogOpen(true); }} 
              className="flex items-center gap-1 sm:gap-2 w-full sm:w-auto mt-2 sm:mt-0"
            >
              <Plus size={14} className="sm:w-4 sm:h-4" />
              <span className="text-xs sm:text-sm">{t('admin.tenders.createTender') || 'إنشاء مناقصة'}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8">
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex flex-col gap-3 sm:gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-slate-400`} size={16} />
                <Input
                  placeholder={t('admin.tenders.searchPlaceholder') || 'البحث عن مناقصة...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`${isRTL ? 'pr-10' : 'pl-10'} text-xs sm:text-sm`}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {/* Status Filter */}
                <div className="w-full">
                  <Select value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
                    <SelectTrigger className="text-xs sm:text-sm">
                      <Filter size={14} className={`${isRTL ? 'ml-2' : 'mr-2'} sm:w-4 sm:h-4`} />
                      <SelectValue placeholder={t('admin.tenders.filterStatus') || 'فلترة حسب الحالة'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('admin.tenders.allStatuses') || 'جميع الحالات'}</SelectItem>
                      <SelectItem value="active">{t('common.active') || 'نشط'}</SelectItem>
                      <SelectItem value="pending">{t('common.pending') || 'قيد المراجعة'}</SelectItem>
                      <SelectItem value="closed">{t('common.closed') || 'مغلق'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Specialization Filter */}
                <div className="w-full">
                  <Select value={specializationFilter} onValueChange={(value: any) => setSpecializationFilter(value)}>
                    <SelectTrigger className="text-xs sm:text-sm">
                      <Briefcase size={14} className={`${isRTL ? 'ml-2' : 'mr-2'} sm:w-4 sm:h-4`} />
                      <SelectValue placeholder={t('admin.tenders.specializations') || 'التخصصات'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('tenders.allSpecializations') || 'جميع التخصصات'}</SelectItem>
                      {specializations?.data?.map((spec: any) => (
                        <SelectItem key={spec.id} value={spec.id.toString()}>
                          {typeof spec.name === 'string' ? spec.name : spec.name[language] || spec.name.en}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tenders Table */}
      <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8">
        {isLoading ? (
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-4 sm:p-6">
              <div className="space-y-3 sm:space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-2 sm:gap-4">
                    <Skeleton className="h-10 w-10 sm:h-12 sm:w-12 rounded" />
                    <div className="space-y-1 sm:space-y-2 flex-1">
                      <Skeleton className="h-3 sm:h-4 w-3/4" />
                      <Skeleton className="h-2 sm:h-3 w-1/2" />
                    </div>
                    <Skeleton className="h-6 sm:h-8 w-16 sm:w-20 rounded" />
                    <Skeleton className="h-6 sm:h-8 w-6 sm:w-8 rounded" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : error ? (
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="pt-4 sm:pt-6">
              <p className="text-center text-slate-500 dark:text-slate-400 text-xs sm:text-sm">
                {t('common.error') || 'حدث خطأ أثناء تحميل المناقصات'}
              </p>
            </CardContent>
          </Card>
        ) : tenders.length === 0 ? (
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="pt-4 sm:pt-6">
              <div className="text-center py-6 sm:py-8">
                <FileText className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-slate-400 mb-3 sm:mb-4" />
                <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm">
                  {t('admin.tenders.noTenders') || 'لا توجد مناقصات'}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-3">{t('admin.tenders.columnTitle') || 'العنوان'}</TableHead>
                      <TableHead className="text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-3 hidden md:table-cell">{t('admin.tenders.deadline') || 'الموعد النهائي'}</TableHead>
                      <TableHead className="text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-3">{t('admin.tenders.status') || 'الحالة'}</TableHead>
                      <TableHead className="text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-3 hidden lg:table-cell">{t('admin.tenders.created') || 'تاريخ الإنشاء'}</TableHead>
                      <TableHead className="text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-3 text-right">{t('admin.tenders.actions') || 'الإجراءات'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tenders.map((tender: Tender) => (
                      <TableRow key={tender.id} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                        <TableCell className="px-2 sm:px-4 py-2 sm:py-3">
                          <div className="min-w-0">
                            <div className="font-medium text-xs sm:text-sm truncate">{getTranslatedText(tender.title)}</div>
                            {tender.description && (
                              <div className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[120px] sm:max-w-xs">
                                {getTranslatedText(tender.description)}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="px-2 sm:px-4 py-2 sm:py-3 hidden md:table-cell">
                          <div className="flex items-center gap-1 text-xs sm:text-sm">
                            <Calendar size={12} className="sm:w-4 sm:h-4" />
                            <span className="truncate max-w-[80px]">
                              {(() => {
                                try {
                                  const date = new Date(tender.deadline);
                                  return format(date, "PP", { locale: isRTL ? ar : enUS });
                                } catch {
                                  return tender.deadline;
                                }
                              })()}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="px-2 sm:px-4 py-2 sm:py-3">{getStatusBadge(tender.status)}</TableCell>
                        <TableCell className="px-2 sm:px-4 py-2 sm:py-3 hidden lg:table-cell">
                          <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                            {(() => {
                              try {
                                const date = new Date(tender.created_at);
                                return format(date, "PP", { locale: isRTL ? ar : enUS });
                              } catch {
                                return tender.created_at;
                              }
                            })()}
                          </div>
                        </TableCell>
                        <TableCell className="px-2 sm:px-4 py-2 sm:py-3 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-6 sm:h-8 w-6 sm:w-8">
                                <MoreVertical size={12} className="sm:w-4 sm:h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align={isRTL ? "start" : "end"} className="w-40 sm:w-48">
                              <DropdownMenuItem onClick={() => handleViewDetails(tender.id)} className="text-xs sm:text-sm">
                                <Eye size={12} className="mr-2 sm:w-4 sm:h-4" />
                                {t('admin.tenders.view') || 'عرض'}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEdit(tender)} className="text-xs sm:text-sm">
                                <Edit size={12} className="mr-2 sm:w-4 sm:h-4" />
                                {t('admin.tenders.edit') || 'تعديل'}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleStatusChange(tender.id, 'active')}
                                disabled={tender.status === 'active'}
                                className="text-xs sm:text-sm"
                              >
                                <CheckCircle size={12} className="mr-2 sm:w-4 sm:h-4" />
                                {t('admin.tenders.activate') || 'تفعيل'}
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleStatusChange(tender.id, 'closed')}
                                disabled={tender.status === 'closed'}
                                className="text-xs sm:text-sm"
                              >
                                <XCircle size={12} className="mr-2 sm:w-4 sm:h-4" />
                                {t('admin.tenders.close') || 'إغلاق'}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleDelete(tender.id)}
                                className="text-xs sm:text-sm text-destructive"
                              >
                                <Trash2 size={12} className="mr-2 sm:w-4 sm:h-4" />
                                {t('admin.tenders.delete') || 'حذف'}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Infinite Scroll Trigger */}
              {hasNextPage && (
                <div ref={observerTarget} className="py-3 sm:py-4">
                  {isFetchingNextPage && (
                    <div className="space-y-2 sm:space-y-4">
                      {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-12 sm:h-16 w-full" />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tender Details Dialog */}
      <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-4xl max-h-[85vh] sm:max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-3 sm:pb-4">
            <DialogTitle className="text-base sm:text-lg">
              {t('admin.tenders.tenderDetails') || 'تفاصيل المناقصة'}
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              {t('admin.tenders.tenderDetailsDesc') || 'معلومات تفصيلية عن المناقصة'}
            </DialogDescription>
          </DialogHeader>
          {detailsLoading ? (
            <div className="space-y-3 sm:space-y-4">
              <Skeleton className="h-16 sm:h-20 w-full" />
              <Skeleton className="h-16 sm:h-20 w-full" />
            </div>
          ) : tenderDetails?.data ? (
            <div className="space-y-4 sm:space-y-6 pt-2 sm:pt-4">
              {/* Basic Info */}
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardHeader className="pb-3 sm:pb-4">
                  <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                    <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
                    {t('admin.tenders.basicInfo') || 'المعلومات الأساسية'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 sm:space-y-4">
                  <div>
                    <Label className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400">
                      {t('admin.tenders.title') || 'العنوان'}
                    </Label>
                    <p className="text-slate-900 dark:text-slate-100 font-semibold mt-1 text-xs sm:text-sm">
                      {getTranslatedText(tenderDetails.data.title)}
                    </p>
                  </div>
                  {tenderDetails.data.description && (
                    <div>
                      <Label className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400">
                        {t('admin.tenders.description') || 'الوصف'}
                      </Label>
                      <p className="text-slate-900 dark:text-slate-100 mt-1 whitespace-pre-wrap text-xs sm:text-sm">
                        {getTranslatedText(tenderDetails.data.description)}
                      </p>
                    </div>
                  )}
                  {tenderDetails.data.requirements && (
                    <div>
                      <Label className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400">
                        {t('admin.tenders.requirements') || 'المتطلبات'}
                      </Label>
                      <p className="text-slate-900 dark:text-slate-100 mt-1 whitespace-pre-wrap text-xs sm:text-sm">
                        {getTranslatedText(tenderDetails.data.requirements)}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Financial Info */}
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardHeader className="pb-3 sm:pb-4">
                  <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                    <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
                    {t('admin.tenders.financialInfo') || 'المعلومات المالية'}
                  </CardTitle>
                </CardHeader>
               
              </Card>

              {/* Status & Dates */}
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <CardHeader className="pb-3 sm:pb-4">
                  <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
                    {t('admin.tenders.statusAndDates') || 'الحالة والتواريخ'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 sm:space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    <div>
                      <Label className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400">
                        {t('common.status') || 'الحالة'}
                      </Label>
                      <div className="mt-1">{getStatusBadge(tenderDetails.data.status)}</div>
                    </div>
                    <div>
                      <Label className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400">
                        {t('admin.tenders.deadline') || 'الموعد النهائي'}
                      </Label>
                      <p className="text-slate-900 dark:text-slate-100 mt-1 text-xs sm:text-sm">
                        {(() => {
                          try {
                            const date = new Date(tenderDetails.data.deadline);
                            return format(date, "PPP", { locale: isRTL ? ar : enUS });
                          } catch {
                            return tenderDetails.data.deadline;
                          }
                        })()}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400">
                        {t('admin.tenders.createdAt') || 'تاريخ الإنشاء'}
                      </Label>
                      <p className="text-slate-900 dark:text-slate-100 mt-1 text-xs sm:text-sm">
                        {(() => {
                          try {
                            const date = new Date(tenderDetails.data.created_at);
                            return format(date, "PPP", { locale: isRTL ? ar : enUS });
                          } catch {
                            return tenderDetails.data.created_at;
                          }
                        })()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex flex-col gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-slate-200 dark:border-slate-700">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleEdit(tenderDetails.data);
                      setIsDetailsDialogOpen(false);
                    }}
                    className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                  >
                    <Edit size={14} className="sm:w-4 sm:h-4" />
                    {t('admin.tenders.edit') || 'تعديل'}
                  </Button>
                  {tenderDetails.data.status === 'pending' && (
                    <Button
                      onClick={() => {
                        handleStatusChange(tenderDetails.data.id, 'active');
                        setIsDetailsDialogOpen(false);
                      }}
                      className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                    >
                      <CheckCircle size={14} className="sm:w-4 sm:h-4" />
                      {t('admin.tenders.activate') || 'تفعيل'}
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                  {tenderDetails.data.status === 'active' && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        handleStatusChange(tenderDetails.data.id, 'closed');
                        setIsDetailsDialogOpen(false);
                      }}
                      className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                    >
                      <XCircle size={14} className="sm:w-4 sm:h-4" />
                      {t('admin.tenders.close') || 'إغلاق'}
                    </Button>
                  )}
                  <Button
                    variant="destructive"
                    onClick={() => {
                      handleDelete(tenderDetails.data.id);
                      setIsDetailsDialogOpen(false);
                    }}
                    className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                  >
                    <Trash2 size={14} className="sm:w-4 sm:h-4" />
                    {t('admin.tenders.delete') || 'حذف'}
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Tender Dialog */}
      <Dialog open={isCreateDialogOpen || isEditDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setIsCreateDialogOpen(false);
          setIsEditDialogOpen(false);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[85vh] sm:max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-3 sm:pb-4">
            <DialogTitle className="text-base sm:text-lg">
              {selectedTenderId 
                ? (t('admin.tenders.editTender') || 'تعديل المناقصة')
                : (t('admin.tenders.createTender') || 'إنشاء مناقصة')
              }
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              {selectedTenderId 
                ? (t('admin.tenders.editTenderDesc') || 'تعديل معلومات المناقصة')
                : (t('admin.tenders.createTenderDesc') || 'إضافة مناقصة جديدة')
              }
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6 pt-2 sm:pt-4">
            {/* Title */}
            <div className="space-y-3 sm:space-y-4">
              <div>
                <Label htmlFor="title-en" className="text-xs sm:text-sm">{t('admin.tenders.titleEn') || 'العنوان (English)'}</Label>
                <Input
                  id="title-en"
                  value={formData.title.en}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    title: { ...prev.title, en: e.target.value }
                  }))}
                  placeholder={t('admin.tenders.titleEnPlaceholder') || 'Enter tender title in English'}
                  required
                  className="text-xs sm:text-sm"
                />
              </div>

              <div>
                <Label htmlFor="title-ar" className="text-xs sm:text-sm">{t('admin.tenders.titleAr') || 'العنوان (العربية)'}</Label>
                <Input
                  id="title-ar"
                  value={formData.title.ar}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    title: { ...prev.title, ar: e.target.value }
                  }))}
                  placeholder={t('admin.tenders.titleArPlaceholder') }
                  required
                  className="text-xs sm:text-sm"
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-3 sm:space-y-4">
              <div>
                <Label htmlFor="description-en" className="text-xs sm:text-sm">{t('admin.tenders.descriptionEn') || 'الوصف (English)'}</Label>
                <Textarea
                  id="description-en"
                  value={formData.description.en}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    description: { ...prev.description, en: e.target.value }
                  }))}
                  placeholder={t('admin.tenders.descriptionEnPlaceholder') || 'Enter tender description in English'}
                  rows={3}
                  className="text-xs sm:text-sm"
                />
              </div>
              <div>
                <Label htmlFor="description-ar" className="text-xs sm:text-sm">{t('admin.tenders.descriptionAr') || 'الوصف (العربية)'}</Label>
                <Textarea
                  id="description-ar"
                  value={formData.description.ar}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    description: { ...prev.description, ar: e.target.value }
                  }))}
                  placeholder={t('admin.tenders.descriptionArPlaceholder')}
                  rows={3}
                  className="text-xs sm:text-sm"
                />
              </div>
            </div>

            {/* Requirements */}
            <div className="space-y-3 sm:space-y-4">
              <div>
                <Label htmlFor="requirements-en" className="text-xs sm:text-sm">{t('admin.tenders.requirementsEn') || 'المتطلبات (English)'}</Label>
                <Textarea
                  id="requirements-en"
                  value={formData.requirements?.en || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    requirements: { ...(prev.requirements || { en: '', ar: '' }), en: e.target.value || '' }
                  }))}
                  placeholder={t('admin.tenders.requirementsEnPlaceholder') || 'Enter tender requirements in English'}
                  rows={2}
                  className="text-xs sm:text-sm"
                />
              </div>
              <div>
                <Label htmlFor="requirements-ar" className="text-xs sm:text-sm">{t('admin.tenders.requirementsAr') || 'المتطلبات (العربية)'}</Label>
                <Textarea
                  id="requirements-ar"
                  value={formData.requirements?.ar || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    requirements: { ...(prev.requirements || { en: '', ar: '' }), ar: e.target.value || '' }
                  }))}
                  placeholder={t('admin.tenders.requirementsArPlaceholder') }
                  rows={2}
                  className="text-xs sm:text-sm"
                />
              </div>
            </div>

            {/* Deadline and Status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label htmlFor="deadline" className="text-xs sm:text-sm">{t('admin.tenders.deadline') || 'الموعد النهائي'}</Label>
                <Input
                  id="deadline"
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    deadline: e.target.value
                  }))}
                  required
                  className="text-xs sm:text-sm"
                />
              </div>
              <div>
                <Label htmlFor="status" className="text-xs sm:text-sm">{t('admin.tenders.statusLabel')}</Label>
                <Select value={formData.status} onValueChange={(value: any) => setFormData(prev => ({
                  ...prev,
                  status: value
                }))}>
                  <SelectTrigger className="text-xs sm:text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">{t('common.pending')}</SelectItem>
                    <SelectItem value="active">{t('common.active')}</SelectItem>
                    <SelectItem value="closed">{t('common.closed')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Visibility Type */}
            <div>
              <Label htmlFor="visibility" className="text-xs sm:text-sm">{t('admin.tenders.visibilityLabel')}</Label>
              <Select value={formData.visibility_type} onValueChange={(value: any) => {
                setFormData(prev => ({
                  ...prev,
                  visibility_type: value,
                  specialization_ids: value === 'specializations' ? prev.specialization_ids : [],
                  client_ids: value === 'specific_clients' ? prev.client_ids : []
                }));
                setSpecializationSearch('');
                setClientSearch('');
              }}>
                <SelectTrigger className="text-xs sm:text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('admin.tenders.allUsers')}</SelectItem>
                  <SelectItem value="specializations">{t('admin.tenders.specializationsOnly')}</SelectItem>
                  <SelectItem value="specific_clients">{t('admin.tenders.specificClients')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Conditional Visibility Fields */}
            {formData.visibility_type === 'specializations' && (
              <div>
                <Label className="text-xs sm:text-sm">{t('admin.tenders.specializations')}</Label>
                <div className="mt-2">
                  <Input
                    placeholder={t('admin.tenders.searchSpecializations')}
                    className="mb-2 text-xs sm:text-sm"
                    value={specializationSearch}
                    onChange={(e) => setSpecializationSearch(e.target.value)}
                  />
                  <div className="max-h-32 overflow-y-auto border rounded-md p-2">
                    {filteredSpecializations.map((spec: any) => (
                      <div
                        key={spec.id}
                        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer rounded"
                        onClick={() => {
                          if (!formData.specialization_ids?.includes(spec.id)) {
                            setFormData(prev => ({
                              ...prev,
                              specialization_ids: [...(prev.specialization_ids || []), spec.id]
                            }));
                          }
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs sm:text-sm">
                            {typeof spec.name === 'string' ? spec.name : spec.name[language] || spec.name.en}
                          </span>
                          {formData.specialization_ids?.includes(spec.id) && (
                            <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-500" />
                          )}
                        </div>
                      </div>
                    ))}
                    {filteredSpecializations.length === 0 && (
                      <div className="text-center text-slate-500 dark:text-slate-400 py-4 text-xs sm:text-sm">
                        {t('admin.tenders.noSpecializationsFound')}
                      </div>
                    )}
                  </div>
                  {formData.specialization_ids && formData.specialization_ids.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">
                        {t('admin.tenders.selectedSpecializations')}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {formData.specialization_ids.map(id => {
                          const spec = specializationsList.find((s: any) => s.id === id);
                          return spec ? (
                            <Badge key={id} variant="secondary" className="text-xs">
                              {typeof spec.name === 'string' ? spec.name : spec.name[language] || spec.name.en}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFormData(prev => ({
                                    ...prev,
                                    specialization_ids: prev.specialization_ids?.filter(sid => sid !== id) || []
                                  }));
                                }}
                                className="ml-1 hover:text-red-500"
                              >
                                ×
                              </button>
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {formData.visibility_type === 'specific_clients' && (
              <div>
                <Label className="text-xs sm:text-sm">{t('admin.tenders.clients')}</Label>
                <div className="mt-2">
                  <Input
                    placeholder={t('admin.tenders.searchClients')}
                    className="mb-2 text-xs sm:text-sm"
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                  />
                  <div className="max-h-32 overflow-y-auto border rounded-md p-2">
                    {filteredClients.map((client: any) => (
                      <div
                        key={client.client_id || client.id}
                        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer rounded"
                        onClick={() => {
                          const clientId = client.client_id || client.id;
                          if (!formData.client_ids?.includes(clientId)) {
                            setFormData(prev => ({
                              ...prev,
                              client_ids: [...(prev.client_ids || []), clientId]
                            }));
                          }
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-xs sm:text-sm font-medium">
                              {client.name || client.company_name || 'Unknown'}
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              {client.email || ''}
                            </div>
                          </div>
                          {formData.client_ids?.includes(client.id) && (
                            <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-500" />
                          )}
                        </div>
                      </div>
                    ))}
                    {filteredClients.length === 0 && (
                      <div className="text-center text-slate-500 dark:text-slate-400 py-4 text-xs sm:text-sm">
                        {t('admin.tenders.noClientsFound')}
                      </div>
                    )}
                  </div>
                  {formData.client_ids && formData.client_ids.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs text-slate-600 dark:text-slate-400 mb-1">
                        {t('admin.tenders.selectedClients')}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {formData.client_ids.map(id => {
                          const client = clientsList.find((c: any) => (c.client_id || c.id) === id);
                          return client ? (
                            <Badge key={id} variant="secondary" className="text-xs">
                              {client.name || client.company_name || 'Unknown'}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFormData(prev => ({
                                    ...prev,
                                    client_ids: prev.client_ids?.filter(cid => cid !== id) || []
                                  }));
                                }}
                                className="ml-1 hover:text-red-500"
                              >
                                ×
                              </button>
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* File Upload */}
            <div>
              <Label className="text-xs sm:text-sm">{t('admin.tenders.documents')}</Label>
              <div className="mt-2 space-y-3 sm:space-y-4 border rounded-md p-3 sm:p-4">
                <div className="flex items-center justify-center w-full">
                  <input
                    type="file"
                    id="file-upload"
                    multiple
                    accept=".pdf,.doc,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const files = e.target.files ? Array.from(e.target.files) : [];
                      if (files.length > 0) {
                        setFormData(prev => ({
                          ...prev,
                          documents: [...(prev.documents || []), ...files] as File[]
                        }));
                      }
                      // Reset input value to allow selecting same file again
                      e.target.value = '';
                    }}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className={`w-full h-20 sm:h-32 border-2 border-dashed ${isDragging ? 'border-primary bg-primary/5' : 'border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500'}`}
                    onClick={() => {
                      const fileInput = document.getElementById('file-upload') as HTMLInputElement;
                      fileInput?.click();
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                      const files = Array.from(e.dataTransfer.files);
                      if (files.length > 0) {
                        setFormData(prev => ({
                          ...prev,
                          documents: [...(prev.documents || []), ...files] as File[]
                        }));
                      }
                    }}
                  >
                    <div className="flex flex-col items-center space-y-1 sm:space-y-2">
                      <Upload className="h-6 w-6 sm:h-8 sm:w-8 text-slate-400" size={20} />
                      <div className="text-center">
                        <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
                          {t('admin.tenders.dragDrop')}
                        </p>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                          {t('admin.tenders.allowedFileTypes')}
                        </p>
                      </div>
                    </div>
                  </Button>
                </div>
                
                {/* File List */}
                {formData.documents && formData.documents.length > 0 && (
                  <div className="mt-3 sm:mt-4">
                    <h4 className="text-xs sm:text-sm font-medium mb-2">{t('admin.tenders.uploadedFiles') || 'الملفات المرفوعة'}</h4>
                    <div className="space-y-2">
                      {formData.documents.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 sm:p-3 bg-slate-50 dark:bg-slate-700 rounded">
                          <div className="flex items-center space-x-2 min-w-0 flex-1">
                            <FileText size={14} className="text-blue-600 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className="text-xs sm:text-sm font-medium truncate">{file.name}</p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {(file.size / 1024).toFixed(1)} KB
                              </p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const updatedDocs = formData.documents?.filter((_, i) => i !== index) || [];
                              setFormData(prev => ({
                                ...prev,
                                documents: updatedDocs
                              }));
                            }}
                            className="flex-shrink-0 h-6 w-6 sm:h-8 sm:w-8"
                          >
                            <X size={14} className="sm:w-4 sm:h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-slate-200 dark:border-slate-700">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  setIsEditDialogOpen(false);
                  resetForm();
                }}
                className="w-full sm:w-auto text-xs sm:text-sm"
              >
                {t('admin.tenders.cancel') || 'إلغاء'}
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="w-full sm:w-auto text-xs sm:text-sm"
              >
                {createMutation.isPending || updateMutation.isPending
                  ? (t('common.loading') || 'جاري التحميل...')
                  : selectedTenderId
                  ? (t('admin.tenders.update') || 'تحديث')
                  : (t('common.create') || 'إنشاء')
                }
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTenders;
